import { useMemo, useState } from "react";
import { DataTable, FilterBar, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta } from "../../components/ui/primitivos";
import { type FlagConsola, fetchFlags, guardarFlag } from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";

/**
 * Consola de feature flags (ADR 0025).
 *
 * Lo que se ve acá es el catálogo que vive en el worker, no una lista propia:
 * la granularidad es la misma del RBAC —`recurso.accion`— más las reglas de
 * negocio declaradas aparte.
 *
 * **Apagar un flag no reemplaza a un permiso.** Un endpoint apagado se cae para
 * todos, incluido quien tiene el permiso; y al revés, prender un flag no le da
 * acceso a nadie. Por eso la pantalla habla de "disponible", no de "permitido".
 */
export function FlagsPagina() {
  const flags = useDatos(fetchFlags);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState("");
  const [trabajando, setTrabajando] = useState<string | null>(null);

  const visibles = useMemo(() => {
    const datos = flags.datos ?? [];
    const q = filtro.trim().toLowerCase();
    const filtrados = q
      ? datos.filter(
          (f) => f.clave.toLowerCase().includes(q) || f.descripcion.toLowerCase().includes(q),
        )
      : datos;
    // Lo modificado primero: es lo que alguien decidió cambiar y lo que hay que
    // revisar cuando algo "no anda".
    return [...filtrados].sort((a, b) => Number(b.modificado) - Number(a.modificado));
  }, [flags.datos, filtro]);

  async function alternar(flag: FlagConsola) {
    setError(null);
    let motivo: string | undefined;

    // Apagar exige explicación —el worker la valida igual—; volver a prender no.
    if (flag.habilitado) {
      const respuesta = window.prompt(`¿Por qué se apaga "${flag.clave}"?`);
      if (respuesta === null) return;
      if (respuesta.trim().length === 0) {
        setError("Apagar un flag requiere un motivo");
        return;
      }
      motivo = respuesta.trim();
    }

    setTrabajando(flag.clave);
    try {
      await guardarFlag(flag.clave, {
        habilitado: !flag.habilitado,
        ...(motivo ? { motivo } : {}),
      });
      await flags.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setTrabajando(null);
    }
  }

  const apagados = (flags.datos ?? []).filter((f) => !f.habilitado).length;

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Feature flags"
        subtitulo="Habilitar o deshabilitar funcionalidad y validaciones sin deploy"
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {flags.error ? <Aviso>{flags.error}</Aviso> : null}

      {apagados > 0 ? (
        <Aviso>
          {apagados} {apagados === 1 ? "flag apagado" : "flags apagados"}. Un cambio tarda hasta 10
          minutos en propagarse a todas las instancias.
        </Aviso>
      ) : null}

      <FilterBar
        busqueda={
          <input
            aria-label="Buscar"
            placeholder="Buscar por clave o descripción..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          />
        }
        filtrosActivos={filtro ? 1 : 0}
        alLimpiar={() => setFiltro("")}
      />

      <DataTable
        datos={visibles}
        clave={(f) => f.clave}
        cargando={flags.cargando}
        mensajeVacio="Ningún flag coincide con la búsqueda"
        columnas={[
          {
            clave: "flag",
            encabezado: "Flag",
            render: (f) => (
              <>
                <div className="font-mono text-sm">{f.clave}</div>
                <div className="text-neutral-500 text-xs">{f.descripcion}</div>
              </>
            ),
          },
          {
            clave: "tipo",
            encabezado: "Tipo",
            render: (f) => <Etiqueta tono="neutral">{f.tipo}</Etiqueta>,
          },
          {
            clave: "estado",
            encabezado: "Estado",
            render: (f) => (
              <>
                <Etiqueta tono={f.habilitado ? "ok" : "error"}>
                  {f.habilitado ? "Habilitado" : "Deshabilitado"}
                </Etiqueta>
                {f.modificado ? (
                  <span className="ml-2 text-neutral-500 text-xs">modificado</span>
                ) : null}
              </>
            ),
          },
          {
            clave: "acciones",
            encabezado: "",
            className: "text-right",
            render: (f) => (
              <Boton
                variante={f.habilitado ? "peligro" : "secundario"}
                disabled={trabajando === f.clave}
                onClick={() => alternar(f)}
              >
                {f.habilitado ? "Deshabilitar" : "Habilitar"}
              </Boton>
            ),
          },
        ]}
      />
    </div>
  );
}
